import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { maskLine, IMAGES } from "@/lib/motion";

const headline = ["Learn Hindi.", "Live it", "with Navjeevan."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const devaY = useTransform(scrollYProgress, [0, 1], [0, -180]);
  const devaRotate = useTransform(scrollYProgress, [0, 1], [0, 12]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen pt-32 md:pt-40 pb-16 overflow-hidden"
    >
      {/* Giant background Devanagari */}
      <motion.span
        style={{ y: devaY, rotate: devaRotate }}
        className="font-deva pointer-events-none select-none absolute -right-10 md:right-4 top-24 text-primary/10 text-[42vw] md:text-[30vw] leading-none z-0"
      >
        अ
      </motion.span>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="overline text-primary mb-8"
        >
          Navjeevan Hindi Institute — est. भाषा · भविष्य
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tighter text-foreground text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.92]">
          {headline.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span
                custom={i}
                variants={maskLine}
                initial="hidden"
                animate="show"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="md:col-span-5 font-body text-base md:text-lg text-muted-foreground leading-relaxed"
          >
            Structured coaching, personalised exam tuition, and career-focused
            spoken Hindi — taught the way a living language deserves to be learnt.
          </motion.p>

          <div className="md:col-span-3 md:col-start-6 flex md:justify-center">
            <motion.button
              data-testid="hero-cta-btn"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.8 }}
              onClick={() =>
                document
                  .querySelector("#courses")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="rounded-full border border-foreground px-8 py-4 font-medium text-sm hover:bg-foreground hover:text-background transition-colors duration-300"
            >
              Explore Courses →
            </motion.button>
          </div>

          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="md:col-span-4 clip-frame rounded-sm"
          >
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <img
                src={IMAGES.heroStudent}
                alt="Student learning at Navjeevan"
                className="w-full h-[280px] md:h-[320px] object-cover"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
